// src/pages/MomentsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config/config';
import LoadingModal from '../components/modals/LoadingModal';

const MomentsPage = () => {
  const { eventId } = useParams();
  const { currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [moments, setMoments] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupedMoments, setGroupedMoments] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if auth is still loading
      if (authLoading) return;
      
      // Check authentication
      if (!isAuthenticated || !currentUser?.token) {
        setError('Please log in to view your moments.');
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        let momentsData = [];
        
        console.log(`Fetching user moments for event ID: ${eventId}`);
        console.log('Current user:', currentUser);
        
        // If eventId is provided, fetch user-specific moments for that event
        if (eventId) {
          // Fetch user's specific moments for this event (from CSV processing)
          const response = await axios.get(
            `${config.BASE_URL}/api/footage/user-moments/${eventId}`, 
            { 
              headers: { 
                Authorization: `Bearer ${currentUser.token}` 
              } 
            }
          );
          
          if (response.data && response.data.success && response.data.moments) {
            momentsData = response.data.moments;
            
            // Also fetch event details
            try {
              const eventResponse = await axios.get(`${config.BASE_URL}/api/events/${eventId}`);
              if (eventResponse.data) {
                setEvent(eventResponse.data);
              }
            } catch (eventError) {
              console.warn('Could not fetch event details:', eventError);
            }
          } else {
            // No moments found for this user in this event
            momentsData = [];
            console.log('No moments found for user in this event');
            
            // Try to get event details even if no moments
            try {
              const eventResponse = await axios.get(`${config.BASE_URL}/api/events/${eventId}`);
              if (eventResponse.data) {
                setEvent(eventResponse.data);
              }
            } catch (eventError) {
              console.warn('Could not fetch event details:', eventError);
            }
          }
        } 
        // Otherwise fetch all moments for the current user across all events
        else {
          console.log('Fetching all moments for current user');
          const response = await axios.get(
            `${config.BASE_URL}/api/footage/user`, 
            { 
              headers: { 
                Authorization: `Bearer ${currentUser.token}` 
              } 
            }
          );
          
          if (response.data && response.data.success && response.data.footages) {
            momentsData = response.data.footages;
          } else {
            momentsData = [];
          }
        }
        
        // Process the URLs to ensure they're complete and use correct base URL
        const processedMoments = momentsData.map(moment => {
          let completeUrl;
          
          if (moment.url && moment.url.startsWith('http')) {
            completeUrl = moment.url;
          } else if (moment.url && moment.url.startsWith('/')) {
            completeUrl = `${config.BASE_URL}${moment.url}`;
          } else if (moment.url) {
            completeUrl = `${config.BASE_URL}/${moment.url}`;
          } else {
            completeUrl = null;
          }
          
          return {
            ...moment,
            url: completeUrl
          };
        }).filter(moment => moment.url);
        
        setMoments(processedMoments);
        
        // Group moments by event_id or event_title
        const grouped = processedMoments.reduce((acc, moment) => {
          const key = moment.event_title || `Event ${moment.event_id}`;
          
          if (!acc[key]) {
            acc[key] = {
              eventId: moment.event_id,
              eventTitle: moment.event_title || `Event ${moment.event_id}`,
              moments: []
            };
          }
          
          acc[key].moments.push(moment);
          return acc;
        }, {});
        
        setGroupedMoments(grouped);
      } catch (err) {
        console.error('Error fetching moments:', err);
        
        // Handle specific error cases
        if (err.response?.status === 404) {
          // This is normal - no CSV file exists yet
          setError(null);
        } else if (err.response?.status === 401) {
          setError('Please log in to view your moments.');
        } else {
          setError('Failed to load moments. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId, currentUser, isAuthenticated, authLoading]);

  // Show loading while auth is being checked
  if (authLoading) {
    return <LoadingModal message="Loading..." />;
  }

  // Check authentication after loading is complete
  if (!isAuthenticated || !currentUser?.token) {
    return (
      <section id="moments" className="pt-4 pb-4">
        <div className="container" style={{ paddingTop: '100px' }}>
          <div className="alert alert-warning">
            <h4>Authentication Required</h4>
            <p>Please log in to view your moments.</p>
            <button className="button" onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="moments" className="pt-4 pb-4" >
      <div className="container" style={{ paddingTop: '100px' }}>
        <div className="row mb-4">
          <div className="col-12">
            <h2 className="text-white">
              {event ? `Your Moments from ${event.event_title}` : 'My Moments'}
            </h2>
          </div>
        </div>

        {loading && (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-white mt-3">Loading your moments...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger text-white">
            {error}
          </div>
        )}

        {!loading && !error && Object.keys(groupedMoments).length === 0 ? (
          <div className="alert alert-info text-white">
            {event ? (
              <div>
                <h5><i className="fa fa-info-circle me-2"></i>Processing Your Moments</h5>
                <p className="mb-2">Your moments from <strong>{event.event_title}</strong> are being processed.</p>
                <p className="mb-2">This usually takes a few minutes after the event photos are uploaded and face recognition processing is complete.</p>
                <p className="mb-0">Please check back in a little while to see your personalized moments!</p>
              </div>
            ) : (
              <div>
                <h5><i className="fa fa-photo me-2"></i>No Moments Yet</h5>
                <p className="mb-0">You don't have any moments yet. Moments will appear here after you attend events and photos are processed.</p>
              </div>
            )}
          </div>
        ) : !loading && !error ? (
          // Loop through each event group
          Object.keys(groupedMoments).map(eventKey => (
            <div key={eventKey} className="event-moments-group mb-5">
              <div className="row">
                <div className="col-12">
                  <h3 className="text-white mb-3 border-bottom border-danger pb-2">
                    {eventKey}
                    <span className="badge bg-danger ms-2">
                      {groupedMoments[eventKey].moments.length} photo{groupedMoments[eventKey].moments.length !== 1 ? 's' : ''}
                    </span>
                  </h3>
                </div>
              </div>
              
              <div className="row">
                {groupedMoments[eventKey].moments.map((moment, index) => (
                  <div key={index} className="col-md-4 col-sm-6 mb-4">
                    <div className="card moment-card bg_grey border-0 h-100">
                      <img
                        src={moment.url}
                        alt={`Moment ${index + 1}`}
                        className="card-img-top"
                        style={{ 
                          height: '240px',
                          objectFit: 'cover',
                          objectPosition: 'center',
                        }}
                        onError={(e) => {
                          console.error(`Failed to load image: ${moment.url}`);
                          e.target.src = 'https://placehold.co/400x240?text=Image+Not+Found';
                        }}
                      />
                      <div className="card-body">
                        <h5 className="card-title text-white">Moment {index + 1}</h5>
                        <a 
                          href={moment.url} 
                          className="button"
                          target="_blank" 
                          rel="noopener noreferrer"
                          download
                        >
                          <i className="fa fa-download me-2"></i> Download
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : null}
      </div>

      {/* Add custom styling for alerts */}
      <style jsx="true">{`
        .alert-danger {
          background-color: #dc3545 !important;
          border-color: #dc3545 !important;
          color: #ffffff !important;
        }
        
        .alert-warning {
          background-color: #ffc107 !important;
          border-color: #ffc107 !important;
          color: #000000 !important;
        }
        
        .alert-info {
          background-color: #0dcaf0 !important;
          border-color: #0dcaf0 !important;
          color: #000000 !important;
        }
        
        .alert-success {
          background-color: #198754 !important;
          border-color: #198754 !important;
          color: #ffffff !important;
        }
      `}</style>
    </section>
  );
};

export default MomentsPage;
